import { Notification } from '@/components/Atoms/Notification/Notification';
import { TitleLink } from '@/components/Atoms/TitleLink/TitleLink';
import { H4 } from '@/components/Core/Heading';
import type { IBreadcrumbItem } from '@/components/Molecules/Breadcrumb/Breadcrumb';
import { ContentTypeHeader } from '@/components/Molecules/ContentTypeHeader/ContentTypeHeader';
import { HeadMeta } from '@/components/Molecules/HeadMeta/HeadMeta';
import { SaveButton } from '@/components/Molecules/SaveButton/SaveButton';
import { SecondaryPanel } from '@/components/Molecules/SecondaryPanel/SecondaryPanel';
import { Stack } from '@/components/Molecules/Stack/Stack';
import { ArticlePageHeader } from '@/components/Organisms/ArticlePageHeader/ArticlePageHeader';
import { ContentItemCard } from '@/components/Organisms/ContentItemCard/ContentItemCard';
import { ContentTranslation } from '@/components/Organisms/ContentTranslation/ContentTranslation';
import { MachineTranslationDisclaimer } from '@/components/Organisms/ContentTranslation/MachineTranslationDisclaimer';
import { Contributors } from '@/components/Organisms/Contributors/Contributors';
import { RSFViewer } from '@/components/Organisms/RSFViewer/RSFViewer';
import {
    ArticlePageAside,
    ArticlePageMain,
    ArticlePageTemplate,
} from '@/components/Templates/ArticlePageTemplate/ArticlePageTemplate';
import { InfiniteScrollPage } from '@/components/Templates/InfiniteScrollPage/InfiniteScrollPage';
import type { IPageItemFetcher, IPageRendererProps } from '@/components/Templates/InfiniteScrollPage/ItemPageRenderer';
import { SailthruTracking } from '@/hooks/useSailthruTracking';
import useTranslation from '@/hooks/useTranslation';
import { useUserContentViewTracker } from '@/hooks/useUserContentViewTracker';
import type { IContentItem, IContentSearch, IFormChoice } from '@/interfaces';
import { ContentType } from '@/interfaces';
import { buildTrackingObject } from '@/services/Analytics/bbAnalyticsTrackers';
import { TrackedProductTypes, TrackingAction, TrackingCategory } from '@/services/Analytics/bnefAnalyticsSettings';
import { BrowserHttpClient } from '@/services/Http/BrowserHttpClient';
import { SearchContentType } from '@/services/Search';
import type { Theme } from 'bnef';
import type { Translate } from 'next-translate';
import type { FC } from 'react';
import { useId } from 'react';

export interface IThemePageProps {
    theme: Theme;
    relatedThemes: IContentItem[];
    translationLocales?: IFormChoice[];
}

export const themesBreadcrumbs = (t: Translate): IBreadcrumbItem[] => [
    { url: '/themes', label: t('common:Sections.themes') },
];

export const ThemePageAnalyticsInfo = {
    pageName: 'BNEF: Themes: Content View',
    siteSection: 'BNEF: Themes',
    type: TrackedProductTypes.Theme,
};

export const themePageFetcher: IPageItemFetcher<IThemePageProps> = async (id: string) => {
    const pageData = await new BrowserHttpClient().get({
        path: '/themes/[ID]',
        replacements: { id: id.toLowerCase() },
    });
    if (pageData?.theme) {
        SailthruTracking.saveTags(id, pageData.theme.type, pageData.theme.tags);
        return { pageData: pageData as IThemePageProps };
    } else if (pageData?.canonicalResource) {
        return { canonicalResource: pageData.canonicalResource };
    }
    return null;
};
export const ThemePageRenderer = ({ pageData, isActive }: IPageRendererProps<IThemePageProps>) => {
    useUserContentViewTracker(pageData.theme.id, ContentType.theme, isActive);
    const { t } = useTranslation();
    const disclaimerId = useId();
    return (
        <>
            <ArticlePageHeader
                title={pageData.theme.title}
                date={pageData.theme.displayDate}
                breadCrumbs={themesBreadcrumbs(t)}
                actions={[
                    <SaveButton
                        key="save"
                        id={pageData.theme.id}
                        type={pageData.theme.type}
                        itemName={pageData.theme.title}
                        analyticsBaseId={`Theme: ${pageData.theme.id}`}
                    />,
                ]}
            />
            <ArticlePageTemplate>
                <ArticlePageMain>
                    {pageData.theme.isArchived && (
                        <Notification state="warning">{t('common:PageBanner.archiveNote')}</Notification>
                    )}
                    <ContentTranslation
                        originalContentLanguage={pageData.theme.originalLanguage}
                        targetDestination={pageData.theme.url}
                        currentTranslationDetails={pageData.theme?.translationDetails ?? null}
                        languageOptions={pageData.translationLocales ?? []}
                        disclaimerId={disclaimerId}
                    />
                    <RSFViewer htmlContent={pageData.theme.body} />
                    <MachineTranslationDisclaimer
                        id={disclaimerId}
                        translationDetails={pageData.theme?.translationDetails}
                    />
                </ArticlePageMain>
                <ArticlePageAside>
                    <DataAndTools theme={pageData.theme} />
                    {pageData.theme.byPeople?.length > 0 && <Contributors items={pageData.theme.byPeople} />}
                </ArticlePageAside>
            </ArticlePageTemplate>
        </>
    );
};

const getAllThemesQuery = (query?: Record<string, any>): IContentSearch => {
    return {
        q: '',
        page: '1',
        pageSize: '60',
        rankingMethod: !query ? 'date' : undefined,
        contentType: SearchContentType.Theme,
        dateFrom: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
        dateTo: new Date(Date.now()).toISOString(),
        ...query,
    };
};

const resultsBuilder = async (initialItem: IContentItem, query?: Record<string, any>): Promise<IContentItem[]> => {
    const resultItems: IContentItem[] = [];
    try {
        const searchResults = await new BrowserHttpClient().get({
            path: '/content_search',
            query: getAllThemesQuery(query),
        });

        if (searchResults?.items) {
            searchResults.items.forEach((item: IContentItem) => {
                if (item.id.toString() !== initialItem.id) {
                    resultItems.push(item);
                }
            });
        }
        return resultItems;
    } catch (e) {
        console.warn('Failed to fetch from /content_search: ' + e.message);
        throw e;
    }
};

export const ThemesViewPage = (initialData: IThemePageProps) => {
    return (
        <>
            <HeadMeta
                title={initialData.theme.title}
                type={ContentType.theme}
                image={initialData.theme.leadImage.baseUrl}
                tags={initialData.theme.tags}
            />
            <InfiniteScrollPage
                initialPageData={initialData}
                resultsBuilder={resultsBuilder}
                currentItem={initialData.theme}
            />
        </>
    );
};

const DataAndTools: FC<{ theme: Theme }> = ({ theme }) => {
    const { t } = useTranslation();
    if (!theme.interactiveDatasets?.length && !theme.terminalLinks?.length) {
        return null;
    }
    return (
        <SecondaryPanel title={t('SiteSection.data_and_tools')}>
            <Stack as="ul">
                {theme.interactiveDatasets.map((entry, index) => {
                    return (
                        <li key={entry.id}>
                            <ContentItemCard
                                item={entry}
                                analyticsCategory={TrackingCategory.RelatedContent}
                                analyticsBaseId={`Related Tools: ${theme.id}: Dataset: ${index}`}
                                headingLevel="h3"
                                responsive={false}
                            />
                        </li>
                    );
                })}
                {theme.terminalLinks.map((entry, index) => {
                    return (
                        <li key={entry.url}>
                            <div
                                data-analytics-tracking={buildTrackingObject(
                                    TrackingAction.FollowLink,
                                    TrackingCategory.RelatedContent,
                                    `Related Tools: ${theme.id}: Terminal: ${index}`
                                )}
                            >
                                <ContentTypeHeader type={ContentType.terminal} />
                                <TitleLink href={entry.url} target="_blank" rel="noopener noreferrer">
                                    <H4 as="h3">{entry.title}</H4>
                                </TitleLink>
                            </div>
                        </li>
                    );
                })}
            </Stack>
        </SecondaryPanel>
    );
};
